import { apiClient } from "@/lib/api/client"
import { apiUrl } from "@/lib/api/url"

// The formats the download menus offer. All three carry the same information:
// JSON is the serialized lead verbatim, CSV and Excel open with the campaign's
// pitch-sheet columns and then mirror that record column by column.
export type LeadExportFormat = "json" | "csv" | "xlsx"

// Filename slug of a company: the backend names the file the same way, but the
// blob reaches us stripped of its Content-Disposition, so the client renames it.
function slugify(value: string): string {
  return value
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
}

export function leadExportFilename(
  lead: { id: number; name: string; normalized_domain: string },
  format: LeadExportFormat,
): string {
  const slug = slugify(lead.name || lead.normalized_domain) || `lead-${lead.id}`
  return `${slug}.${format}`
}

export function campaignExportFilename(
  campaignId: number,
  campaignName: string,
  format: LeadExportFormat,
): string {
  const slug = slugify(campaignName) || `campaign-${campaignId}`
  return `${slug}.${format}`
}

// Fetch an export (with auth/refresh) and hand the bytes to the browser.
async function download(url: string, filename: string): Promise<void> {
  const blob = await apiClient<Blob>({
    url,
    method: "GET",
    // The JSON export answers `application/json`: without this the client would
    // parse it into an object instead of handing over the file's bytes.
    responseType: "blob",
  })
  const objectUrl = URL.createObjectURL(blob)
  const anchor = document.createElement("a")
  anchor.href = objectUrl
  anchor.download = filename
  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()
  URL.revokeObjectURL(objectUrl)
}

export function downloadLeadExport(
  leadId: number,
  format: LeadExportFormat,
  filename: string,
): Promise<void> {
  return download(
    apiUrl(`api/al/lead/${leadId}/export/?file_format=${format}`),
    filename,
  )
}

// Every lead of the campaign in one file, or only ``leadIds`` when given (the
// leads ticked in the list).
export function downloadCampaignExport(
  campaignId: number,
  format: LeadExportFormat,
  filename: string,
  leadIds?: number[],
): Promise<void> {
  const selection = leadIds?.length ? `&lead_ids=${leadIds.join(",")}` : ""
  return download(
    apiUrl(
      `api/al/campaign/${campaignId}/export/?file_format=${format}${selection}`,
    ),
    filename,
  )
}
